/**
 * @copyright (C) 2024, rsupport. All rights reserved
 *
 * @license
 * This software and/or source code may be used, copied and/or disseminated only
 * with the written permission of rsupport, or in accordance with the terms
 * and conditions stipulated in the agreement/contract under which the software
 * and/or source code has been supplied by rsupport or its affiliates.
 * Unauthorized use, copying, or dissemination of this file, via any medium, is
 * strictly prohibited, and will constitute an infringement of copyright.
 *
 * @author
 * Soyun Lee (sylee2@rsupport.com)
 *
 * @description
 *
 * @created at Tue Sep 24 2024
 **/
import { useState, useEffect, useCallback } from "react";
import { Subscription, timer } from "rxjs";

export type LongPressType = {
	onMouseDown: (e: React.MouseEvent<HTMLButtonElement>) => void;
	onMouseUp: (e: React.MouseEvent<HTMLButtonElement>) => void;
	onMouseLeave: (e: React.MouseEvent<HTMLButtonElement>) => void;
	onTouchStart: (e: React.TouchEvent<HTMLButtonElement>) => void;
	onTouchEnd: (e: React.TouchEvent<HTMLButtonElement>) => void;
	onDragEnd: (e: React.MouseEvent<HTMLButtonElement>) => void;
};

interface LongPressPropsType {
	startCallback: () => void;
	EndCallback?: () => void;
	ms?: number;
}

let timerId: Subscription | null = null;

export default function useLongPress({ startCallback, EndCallback, ms = 1000 }: LongPressPropsType) {
	const [isStart, setStart] = useState(false);
	const [startLongPress, setStartLongPress] = useState(false);
	const [isActivated, setIsActivated] = useState(false);

	useEffect(() => {
		if (startLongPress) {
			timerId = timer(ms).subscribe(() => {
				startCallback();
				setIsActivated(true);
			});

			if (!isStart) {
				setStart(true);
			}
		} else {
			if (isStart) {
				EndCallback && EndCallback();
				if (!timerId) return;
				timerId?.unsubscribe();
			}
		}
		return () => {
			if (!timerId) return;
			timerId.unsubscribe();
		};
	}, [startCallback, ms, startLongPress, isStart]);

	const start = useCallback(() => {
		setIsActivated(false);
		setStartLongPress(true);
	}, []);

	const stop = useCallback(() => {
		setStartLongPress(false);
	}, []);

	return {
		onMouseDown: start,
		onMouseUp: stop,
		onMouseLeave: stop,
		onTouchStart: start,
		onTouchEnd: stop,
		onDragEnd: stop,
		isActivated
	};
}
